/**
 * Centralized API layer for SecureSight frontend.
 * All API calls go through this module. baseURL from env REACT_APP_BACKEND_URL/api
 * Auth token auto-injected from sessionStorage when present.
 */

import axios from "axios";

const baseURL = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

const api = axios.create({
  baseURL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || "";
      const isAuthRequest = /\/auth\/(login|register)$/.test(requestUrl.replace(/^\//, ""));
      if (!isAuthRequest) {
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("user");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);

// ==================== AUTH ====================

export const register = (userData) =>
  api
    .post("/auth/register", userData, { timeout: 20000 })
    .then((res) => res.data);

export const login = (credentials) =>
  api.post("/auth/login", credentials).then((res) => res.data);

export const getCurrentUser = () => api.get("/auth/me").then((res) => res.data);

export const changePassword = (currentPassword, newPassword) =>
  api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword }).then((res) => res.data);

export const forgotPassword = (email) =>
  api.post("/auth/forgot-password", { email }).then((res) => res.data);

export const resetPassword = (token, newPassword) =>
  api.post("/auth/reset-password", { token, new_password: newPassword }).then((res) => res.data);

export const verify2FA = (code) =>
  api.post("/auth/2fa/verify", { code }).then((res) => res.data);

export const disable2FA = (code) =>
  api.post("/auth/2fa/disable", { code }).then((res) => res.data);

// ==================== DEVICES ====================

export const getDevices = (pageNumber = 1, pageSize = 20, clientId = null) =>
  api
    .get("/devices/", {
      params: { page_number: pageNumber, page_size: pageSize, ...(clientId && { client_id: clientId }) },
    })
    .then((res) => res.data);

export const createDevice = (device) =>
  api.post("/devices/", device).then((res) => res.data);

export const updateDevice = (id, device) =>
  api.put(`/devices/${id}`, device).then((res) => res.data);

export const deleteDevice = (id) =>
  api.delete(`/devices/${id}`).then((res) => res.data);

// ==================== ALERTS ====================

export const getAlerts = (pageNumber = 1, pageSize = 20, clientId = null) =>
  api
    .get("/alerts/", {
      params: { page_number: pageNumber, page_size: pageSize, ...(clientId && { client_id: clientId }) },
    })
    .then((res) => res.data);

export const createAlert = (alert) =>
  api.post("/alerts/", alert).then((res) => res.data);

export const updateAlertStatus = (alertId, status) =>
  api
    .put(`/alerts/${alertId}/status`, { status })
    .then((res) => res.data);

export const bulkUpdateAlertStatus = (alertIds, status) =>
  api.post("/alerts/bulk-status", { alert_ids: alertIds, status }).then((res) => res.data);

export const deleteAlert = (alertId) =>
  api.delete(`/alerts/${alertId}`).then((res) => res.data);

// ==================== DASHBOARD ====================

export const getDashboardStats = (clientId = null) =>
  api.get("/dashboard/stats", { params: clientId ? { client_id: clientId } : {} }).then((res) => res.data);

export const getUnreadAlertCount = () =>
  api.get("/alerts/unread-count").then((res) => res.data);

// ==================== USER ACTIVITY ====================

export const getUserActivity = (pageNumber = 1, pageSize = 20) =>
  api
    .get("/user-activity/", {
      params: { page_number: pageNumber, page_size: pageSize },
    })
    .then((res) => res.data);

// ==================== COMPLIANCE ====================

export const getCompliance = (pageNumber = 1, pageSize = 20, clientId = null) =>
  api
    .get("/compliance/", {
      params: { page_number: pageNumber, page_size: pageSize, ...(clientId && { client_id: clientId }) },
    })
    .then((res) => res.data);

// ==================== NETWORK ====================

export const getNetworkTraffic = (pageNumber = 1, pageSize = 20) =>
  api
    .get("/network/traffic", {
      params: { page_number: pageNumber, page_size: pageSize },
    })
    .then((res) => res.data);

// ==================== BACKUPS ====================

export const getBackups = (pageNumber = 1, pageSize = 20, status = null) =>
  api
    .get("/backups/", {
      params: { page_number: pageNumber, page_size: pageSize, ...(status && { status }) },
    })
    .then((res) => res.data);

export const getBackupSummary = () =>
  api.get("/backups/summary").then((res) => res.data);

export const createBackup = (data) =>
  api.post("/backups/", data).then((res) => res.data);

export const updateBackup = (backupId, data) =>
  api.put(`/backups/${backupId}`, data).then((res) => res.data);

export const deleteBackup = (backupId) =>
  api.delete(`/backups/${backupId}`).then((res) => res.data);

export const triggerBackup = (deviceId) =>
  api.post(`/backups/${deviceId}/trigger`).then((res) => res.data);

// ==================== PASSWORD HEALTH ====================

export const getPasswordHealth = (pageNumber = 1, pageSize = 20) =>
  api
    .get("/password-health/", {
      params: { page_number: pageNumber, page_size: pageSize },
    })
    .then((res) => res.data);

export const checkPasswordBreach = (password) =>
  api
    .post("/password-health/hibp/check-password", { password })
    .then((res) => res.data);

export const checkEmailBreach = (email) =>
  api
    .post("/password-health/hibp/check-email", { email })
    .then((res) => res.data);

export const scanAllForBreaches = () =>
  api.post("/password-health/hibp/scan-all").then((res) => res.data);

// ==================== DEVICE HEALTH ====================

export const getDeviceHealth = (pageNumber = 1, pageSize = 20) =>
  api
    .get("/device-health/", {
      params: { page_number: pageNumber, page_size: pageSize },
    })
    .then((res) => res.data);

export const getDeviceHealthById = (deviceId) =>
  api.get(`/device-health/${deviceId}`).then((res) => res.data);

// ==================== PEOPLE ====================

export const getPeople = (pageNumber = 1, pageSize = 20) =>
  api
    .get("/people/", {
      params: { page_number: pageNumber, page_size: pageSize },
    })
    .then((res) => res.data);

export const createPerson = (person) =>
  api.post("/people/", person).then((res) => res.data);

export const updatePerson = (personId, person) =>
  api.put(`/people/${personId}`, person).then((res) => res.data);

export const deletePerson = (personId) =>
  api.delete(`/people/${personId}`).then((res) => res.data);

export const checkPersonBreach = (personId) =>
  api.post(`/people/${personId}/check-breach`).then((res) => res.data);

// ==================== SUBSCRIPTION ====================

export const getSubscriptionTiers = (deviceCount) =>
  api.get("/subscription/tiers", { params: { device_count: deviceCount } });

export const getSubscriptionStatus = () => api.get("/subscription/status");

export const createCheckout = (data) =>
  api.post("/subscription/checkout", data);

// ==================== AGENT ====================

export const getAgentTokens = () => api.get("/agent/tokens");

export const createAgentToken = (data) => api.post("/agent/tokens", data);

export const revokeAgentToken = (tokenId) =>
  api.delete(`/agent/tokens/${tokenId}`);

export const getAgentDevices = () => api.get("/agent/devices");

// ==================== REPORTS ====================

export const getReports = () => api.get("/reports/");

// ==================== SETTINGS ====================

export const getSettings = () => api.get("/settings/");

// ==================== BUG REPORTS ====================

export const submitBugReport = (data) =>
  api.post("/bug-reports/report", data).then((res) => res.data);

export const testEmail = () => api.post("/bug-reports/test-email");

// ==================== AI ====================

export const getAIInsights = () => api.get("/ai/insights");

// AI sub-endpoints (may not exist yet - for AIInsightsPanel)
export const getAIDashboardInsights = () => api.get("/ai/dashboard-insights");

export const getAIPasswordAnalysis = () => api.get("/ai/password-analysis");

export const getAINetworkAnalysis = () => api.get("/ai/network-analysis");

export const getAIComplianceReport = () => api.get("/ai/compliance-report");

export const getAIAlertsPriority = () => api.get("/ai/alerts-priority");

// ==================== CHAT ====================

export const sendChat = (message, sessionId) =>
  api.post("/chat", { message, session_id: sessionId });

export const getQuickInsights = () => api.get("/chat/quick-insights");

// ==================== THREATS ====================

export const getThreats = () => api.get("/threats/");

export const analyzeThreat = (event) => api.post("/threats/analyze", event);

export const resolveThreat = (threatId) =>
  api.post(`/threats/${threatId}/resolve`);

export const getAvailableFixes = () => api.get("/threats/autofix/available");

export const executeFix = (data) => api.post("/threats/autofix/execute", data);

// AutoFix endpoints (history/recommend may not exist yet)
export const getAutofixHistory = () => api.get("/threats/autofix/history");

export const getAutofixRecommend = (data) =>
  api.post("/threats/autofix/recommend", data);

export const sendThreatChat = (message, sessionId) =>
  api.post("/threats/chat", { message, session_id: sessionId });

// Threat endpoints (some may not exist yet - for ThreatDetectionPage)
export const getThreatStats = () => api.get("/threats/stats");

export const getBlockedList = () => api.get("/threats/blocked-list");

export const scanAllThreats = () => api.post("/threats/scan-all");

export const blockThreat = (threatId) => api.post(`/threats/${threatId}/block`);

export const blockTarget = (data) => api.post("/threats/block-target", data);

export const unblockTarget = (targetType, targetValue) =>
  api.post(`/threats/unblock/${targetType}/${encodeURIComponent(targetValue)}`);

// ==================== INVITATIONS ====================

export const getInvitations = () => api.get("/invitations/").then((res) => res.data);
export const createInvitation = (data) => api.post("/invitations/", data).then((res) => res.data);
export const revokeInvitation = (inviteId) => api.delete(`/invitations/${inviteId}`).then((res) => res.data);

// ==================== SEED ====================

export const seedData = () => api.post("/seed/data");

// ==================== ADMIN (admin role required) ====================

export const getAdminStats = () =>
  api.get("/admin/stats").then((res) => res.data);

export const getAdminMsps = (pageNumber = 1, pageSize = 20) =>
  api
    .get("/admin/msps", {
      params: { page_number: pageNumber, page_size: pageSize },
    })
    .then((res) => res.data);

export const createMsp = (data) =>
  api.post("/admin/msps", data).then((res) => res.data);

export const updateMspSubscription = (mspId, data) =>
  api.put(`/admin/msps/${mspId}/subscription`, data).then((res) => res.data);

export const updateMspPayment = (mspId, data) =>
  api.put(`/admin/msps/${mspId}/payment`, data).then((res) => res.data);

export const updateMspStatus = (mspId, data) =>
  api.put(`/admin/msps/${mspId}/status`, data).then((res) => res.data);

// ==================== AUDIT LOG ====================
export const getAuditLog = (page = 1, limit = 50, filters = {}) =>
  api.get("/audit/", { params: { page, limit, ...filters } }).then((res) => res.data);
export const getAuditSummary = (days = 7) =>
  api.get("/audit/summary", { params: { days } }).then((res) => res.data);

export const exportAuditLog = (days = 30) =>
  api.get("/audit/export", { params: { days }, responseType: "blob" });

// ==================== ROLES ====================
export const getAvailableRoles = () => api.get("/admin/roles").then((res) => res.data);
export const updateUserRole = (userId, role) =>
  api.put(`/admin/users/${userId}/role`, { role }).then((res) => res.data);

// ==================== SETTINGS EXTENDED (endpoints may not exist yet) ====================

export const getApiKeyStatus = () => api.get("/settings/api-keys/status");

export const saveStripeKey = (secretKey) =>
  api.post("/settings/api-keys/stripe", { secret_key: secretKey });

export const saveHibpKey = (apiKey) =>
  api.post("/settings/api-keys/hibp", { api_key: apiKey });

// ==================== REPORTS EXTENDED (endpoints may not exist yet) ====================

export const createScheduledReport = (data) =>
  api.post("/reports/scheduled", data);

// ==================== SUBSCRIPTION EXTENDED (endpoints may not exist yet) ====================

export const getCheckoutStatus = (sessionId) =>
  api.get(`/subscription/checkout/status/${sessionId}`);

export const startTrial = (data) => api.post("/subscription/start-trial", data);

export const getSubscriptionPricing = (deviceCount, tier) =>
  api.get("/subscription/pricing", { params: { device_count: deviceCount, tier } });

export const cancelSubscription = () =>
  api.post("/subscription/cancel").then((res) => res.data);

export const changeSubscriptionTier = (tier) =>
  api.post("/subscription/change-tier", { tier }).then((res) => res.data);

// ==================== AUTH 2FA (endpoints may not exist yet) ====================

export const get2FAStatus = () => api.get("/auth/2fa/status");

export const setup2FA = () => api.post("/auth/2fa/setup", {});

// ==================== ONBOARDING ====================

export const getOnboardingStatus = () =>
  api.get("/onboarding/status").then((res) => res.data);

export const setupCompanyProfile = (data) =>
  api.post("/onboarding/company-profile", data).then((res) => res.data);

export const setupTeam = (data) =>
  api.post("/onboarding/team", data).then((res) => res.data);

export const setupCompliance = (data) =>
  api.post("/onboarding/compliance", data).then((res) => res.data);

export const completeOnboarding = () =>
  api.post("/onboarding/complete").then((res) => res.data);

export const getAvailableFrameworks = () =>
  api.get("/onboarding/frameworks").then((res) => res.data);

// ==================== LEGACY NAMED EXPORTS (for backward compatibility) ====================

export const authAPI = {
  login,
  register,
  getMe: getCurrentUser,
  changePassword: (data) =>
    api.post("/auth/change-password", data).then((res) => res.data),
};

export const devicesAPI = {
  getAll: getDevices,
  create: createDevice,
  update: updateDevice,
  delete: deleteDevice,
};

export const alertsAPI = {
  getAll: getAlerts,
  create: createAlert,
  updateStatus: updateAlertStatus,
};

export const activityAPI = {
  getAll: getUserActivity,
};

export const complianceAPI = {
  getAll: getCompliance,
};

export const networkAPI = {
  getAll: getNetworkTraffic,
};

export const dashboardAPI = {
  getStats: getDashboardStats,
};

// ==================== EXPORTS ====================
export const exportDevices = (format = "csv") =>
  api.get(`/exports/devices`, { params: { format }, responseType: "blob" });
export const exportAlerts = (format = "csv") =>
  api.get(`/exports/alerts`, { params: { format }, responseType: "blob" });
export const exportCompliance = (format = "csv") =>
  api.get(`/exports/compliance`, { params: { format }, responseType: "blob" });
export const exportSecurityReport = () =>
  api.get(`/exports/security-report`, { responseType: "blob" });

// ==================== OUTBOUND WEBHOOKS ====================
export const getOutboundWebhooks = () => api.get("/webhooks/outbound/").then((res) => res.data);
export const createOutboundWebhook = (data) => api.post("/webhooks/outbound/", data).then((res) => res.data);
export const deleteOutboundWebhook = (id) => api.delete(`/webhooks/outbound/${id}`).then((res) => res.data);
export const testOutboundWebhook = (id) => api.post(`/webhooks/outbound/${id}/test`).then((res) => res.data);

// ==================== SSO ====================
export const getSSOConfig = () => api.get("/sso/config").then((res) => res.data);
export const createSSOConfig = (data) => api.post("/sso/config", data).then((res) => res.data);
export const deleteSSOConfig = () => api.delete("/sso/config").then((res) => res.data);
export const ssoCallback = (data) => api.post("/sso/callback", data).then((res) => res.data);

// ==================== BRANDING ====================
export const getBranding = () => api.get("/branding/").then((res) => res.data);
export const updateBranding = (data) => api.put("/branding/", data).then((res) => res.data);
export const getPublicBranding = (mspId) => api.get(`/branding/public/${mspId}`).then((res) => res.data);

// ==================== CLIENT COMPANIES ====================
export const getClients = () => api.get("/clients/").then((res) => res.data);
export const createClient = (data) => api.post("/clients/", data).then((res) => res.data);
export const getClient = (id) => api.get(`/clients/${id}`).then((res) => res.data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data).then((res) => res.data);
export const deleteClient = (id) => api.delete(`/clients/${id}`).then((res) => res.data);
export const getClientDevices = (id) => api.get(`/clients/${id}/devices`).then((res) => res.data);
export const assignDeviceToClient = (clientId, deviceId) => api.put(`/clients/${clientId}/devices/${deviceId}`).then((res) => res.data);
export const unassignDeviceFromClient = (clientId, deviceId) => api.delete(`/clients/${clientId}/devices/${deviceId}`).then((res) => res.data);
export const getClientSummary = (id) => api.get(`/clients/${id}/summary`).then((res) => res.data);

// ==================== MSP PORTAL ====================
export const getMSPDashboard = () => api.get("/msp/dashboard").then((res) => res.data);
export const getClientTechnicians = (clientId) => api.get(`/clients/${clientId}/technicians`).then((res) => res.data);
export const assignTechnician = (clientId, userId) => api.put(`/clients/${clientId}/technicians/${userId}`).then((res) => res.data);
export const unassignTechnician = (clientId, userId) => api.delete(`/clients/${clientId}/technicians/${userId}`).then((res) => res.data);

export default api;
